import React, { useEffect, useState } from 'react';
import api from '../../../utils/api';

const STATUS_BADGES = {
  paid: 'bg-green-100 text-green-800 ring-green-500/20',
  pending: 'bg-yellow-100 text-yellow-800 ring-yellow-500/20',
  overdue: 'bg-red-100 text-red-800 ring-red-500/20',
  canceled: 'bg-gray-100 text-gray-700 ring-gray-400/20',
};

const Billing = ({ userId }) => {
  const [invoices, setInvoices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emptyState, setEmptyState] = useState(false);
  const [filter, setFilter] = useState('');
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 11;

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const res = await api.get(`/api/users/${userId}/orders`);
        if (!res.data.length) setEmptyState(true);
        setInvoices(res.data);
      } catch (err) {
        console.error('Error al obtener facturas:', err);
        setEmptyState(true);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [userId]);

  useEffect(() => {
    const results = invoices.filter(
      (inv) =>
        (!filter || inv.status === filter) &&
        (!query || inv.invoice_code.toLowerCase().includes(query.toLowerCase()))
    );
    setFiltered(results);
    setCurrentPage(1);
  }, [query, filter, invoices]);

  if (loading) return <p className="p-6 text-center text-sm">Cargando...</p>;

  if (emptyState) {
    return (
      <div className="text-center py-20 text-gray-500">
        <svg className="mx-auto h-10 w-10 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-semibold text-gray-900">No hay facturas disponibles</h3>
        <p className="mt-1 text-sm text-gray-500">Este usuario aún no ha generado ninguna factura.</p>
      </div>
    );
  }

  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  return (
    <div className="space-y-6">
      <div className="flex justify-between gap-4">
        <input
          type="text"
          placeholder="Buscar por código de factura"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-1/2 border border-gray-300 rounded px-3 py-2 text-sm"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-1/3 border border-gray-300 rounded px-3 py-2 text-sm"
        >
          <option value="">Todos los estados</option>
          <option value="paid">Pagadas</option>
          <option value="pending">Pendientes</option>
          <option value="overdue">Vencidas</option>
          <option value="canceled">Canceladas</option>
        </select>
      </div>

      <div className="space-y-4">
        {paginated.map((invoice) => (
          <div
            key={invoice.id}
            className="flex justify-between items-center border border-gray-200 rounded-lg px-4 py-3 bg-white shadow-sm"
          >
            <div>
              <p className="text-sm font-medium text-gray-900">#{invoice.invoice_code}</p>
              <p className="text-xs text-gray-500">
                Fecha: {new Date(invoice.created_at).toLocaleDateString()} — Total: {invoice.total} {invoice.currency}
              </p>
            </div>
            <span
              className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                STATUS_BADGES[invoice.status] || 'bg-gray-100 text-gray-700 ring-gray-400/20'
              }`}
            >
              {invoice.status}
            </span>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center pt-4 text-sm text-gray-600">
        <p>Página {currentPage} de {totalPages}</p>
        <div className="space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
};

export default Billing;
